/*
 * HomeFixer360 - Proposals Management Page
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { FileText, Plus, Eye, Edit, Trash2, Send, Clock, CheckCircle, XCircle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const ProposalsPage = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [proposals, setProposals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (user && isAuthenticated) {
      loadProposals();
    }
  }, [filterStatus, user, isAuthenticated]);

  const loadProposals = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const params = { user_id: user.id };
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const response = await axios.get(`${BACKEND_URL}/api/proposals/`, { params });
      setProposals(response.data);
    } catch (error) {
      console.error('Error loading proposals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      viewed: 'bg-purple-100 text-purple-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      expired: 'bg-orange-100 text-orange-800'
    };

    const icons = {
      draft: <Edit className="w-3 h-3" />,
      sent: <Send className="w-3 h-3" />,
      viewed: <Eye className="w-3 h-3" />,
      accepted: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />,
      expired: <Clock className="w-3 h-3" />
    };

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${styles[status]}`}>
        {icons[status]}
        <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleDelete = async (proposalId) => {
    if (!window.confirm('Are you sure you want to delete this proposal?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/proposals/${proposalId}`);
      loadProposals();
    } catch (error) {
      console.error('Error deleting proposal:', error);
      alert(error.response?.data?.detail || 'Failed to delete proposal');
    }
  };

  const handleStatusChange = async (proposalId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/proposals/${proposalId}/status`, null, {
        params: { new_status: newStatus }
      });
      loadProposals();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update proposal status');
    }
  };

  // Show loading state while authentication is being checked
  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading proposals...</p>
        </div>
      </div>
    );
  }

  // Show error if user is not authenticated
  if (!isAuthenticated || !user) {
    return (
      <div className="max-w-7xl mx-auto p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg">Please log in to access proposals.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Proposals</h1>
          <p className="text-gray-600 mt-1">Manage your customer proposals</p>
        </div>
        <Button
          onClick={() => window.location.href = '/dashboard'}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create New Estimate
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex space-x-1 p-2">
          {['all', 'draft', 'sent', 'viewed', 'accepted', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Proposals List */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading proposals...</p>
        </div>
      ) : proposals.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No proposals yet</h3>
          <p className="text-gray-600 mb-4">Create an estimate first, then generate a proposal</p>
          <Button
            onClick={() => window.location.href = '/dashboard'}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Go to Dashboard
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {proposals.map((proposal) => (
            <div
              key={proposal.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {proposal.customer_name}
                    </h3>
                    {getStatusBadge(proposal.status)}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{proposal.customer_email}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Total Amount</p>
                      <p className="font-medium text-gray-900">{formatCurrency(proposal.pricing.total)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Created</p>
                      <p className="font-medium text-gray-900">{formatDate(proposal.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Valid Until</p>
                      <p className="font-medium text-gray-900">{formatDate(proposal.valid_until)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm bg-gray-50 p-3 rounded">
                    <div>
                      <p className="text-gray-500">Square Footage</p>
                      <p className="font-medium text-gray-900">{proposal.satellite_data.square_footage.toFixed(0)} sq ft</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Roof Type</p>
                      <p className="font-medium text-gray-900">{proposal.satellite_data.roof_type.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Complexity</p>
                      <p className="font-medium text-gray-900">{proposal.satellite_data.complexity}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-2 ml-4">
                  <Button
                    onClick={() => window.location.href = `/proposal/${proposal.id}`}
                    variant="outline"
                    size="sm"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>

                  {proposal.status === 'draft' && (
                    <>
                      <Button
                        onClick={() => handleStatusChange(proposal.id, 'sent')}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Send
                      </Button>
                      <Button
                        onClick={() => handleDelete(proposal.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </>
                  )}

                  {proposal.status === 'sent' && (
                    <Button
                      onClick={() => window.open(`/proposal/${proposal.id}`, '_blank')}
                      variant="outline"
                      size="sm"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProposalsPage;
